import React from 'react';

import { EMPTY_VALUE, formatDate } from '@betterment/core';
import { Box, Text, ToggleTip } from '@betterment/samba-react';
import { useCursor } from '@betterment/shared-components';
import { isNil } from 'es-toolkit/compat';

import { PerformanceHistoryTimeWeightedReturnItem } from '../../../../graphql/schema';

const formatPercentage = (value: number) => {
  const percentage = value / 100;
  const sign = percentage > 0 ? '+' : '';
  return `${sign}${percentage.toFixed(2)}%`;
};

const conditionalStyling = (formattedReturn: string) => {
  if (formattedReturn.startsWith('+')) {
    return 'text.success';
  }
  return 'text.default';
};

export type TimeWeightedReturnsChartDataProps = {
  timeSeries?: PerformanceHistoryTimeWeightedReturnItem[];
  loading?: boolean;
  portfolioDescription?: string;
};

export const TimeWeightedReturnsChartData: React.FC<TimeWeightedReturnsChartDataProps> = ({
  timeSeries,
  loading = false,
  portfolioDescription = undefined,
}) => {
  const { data: cursorData } = useCursor<PerformanceHistoryTimeWeightedReturnItem>();

  if (loading || !timeSeries || timeSeries.length === 0) {
    return null;
  }

  const validEntries = timeSeries.filter(d => d.cumulativeReturn != null);
  const lastEntry = validEntries[validEntries.length - 1];

  if (!cursorData && !lastEntry) {
    return null;
  }

  const displayData = cursorData || lastEntry;
  const displayDate = cursorData ? formatDate(displayData.date) : undefined;
  const returnValue = displayData.cumulativeReturn;

  return (
    <Box css={{ marginBottom: 'space.200' }}>
      <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.100', marginBottom: 'space.050' }}>
        <Box
          css={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: 'dataviz.blue.2',
          }}
        />
        <Text variant="text50">Your portfolio</Text>
        {portfolioDescription && (
          <ToggleTip>
            <ToggleTip.Trigger label="Portfolio Strategy Information" css={{ ml: 'space.50' }} />
            <ToggleTip.Content align="start" side="left" css={{ maxWidth: '300px', width: 'max-content' }}>
              Your current portfolio strategy is <br />
              {portfolioDescription}
            </ToggleTip.Content>
          </ToggleTip>
        )}
      </Box>
      <Box css={{ display: 'flex', flexDirection: 'column' }}>
        {!isNil(returnValue) && (
          <Text variant="text300" css={{ color: conditionalStyling(formatPercentage(returnValue)) }}>
            {formatPercentage(returnValue)}
          </Text>
        )}
        {isNil(returnValue) && (
          <Text variant="text300" css={{ color: 'text.default' }}>
            {EMPTY_VALUE}
          </Text>
        )}
        <Text variant="text50" css={{ color: 'text.secondary' }}>
          {displayDate || '\u00A0'}
        </Text>
      </Box>
    </Box>
  );
};
